import React, { useLayoutEffect, useEffect, useState, useRef, useMemo, useContext } from 'react'
import Circle from 'react-circle'
import DatePicker from 'react-mobile-datepicker'
import MessageBox from '@/components/message-box'
import LoadingPage from '@/components/loading-page'
import LoadingMore from '@/components/loading-more'
import useQuery from '@/hooks/useQuery'
import PopupBottomModal from '@/components/popup-bottom-modal'
import { closeWebview } from '../utils'
import {
  getTaskDetail,
  // getTaskProgress,
  saveScheduleTime,
  cancelScheduleTime,
  awakenVehicle,
  startConditionCheck
} from '../api'

import styles from './index.module.scss'
import './reset.scss'

import scheduleIcon from '../assets/schedule.png'
import arrowRightIcon from '../assets/right_arrow.png'
import loadingIcon from '../assets/loading.png'
import finishedIcon from '../assets/finished.png'
import waitingIcon from '../assets/waiting.png'
import successImg from '../assets/success.png'
import errorImg from '../assets/error.png'
import './_variables.css'
import { ThemeModeContext } from "../../../ThemeModeContext"

interface VehicleOSVersion {
  version: string
  introduces: string[]
  packageSize: string
  isSilentDownload: boolean
}

interface OTATaskDetail {
  otaTaskId: string
  vin: string
  otaStatus: number
  otaEventType: number
  currentOSVersion: VehicleOSVersion
  newOSVersion: VehicleOSVersion
  osVersion: VehicleOSVersion // 自行判断当前是否有可升级任务来决定是用currentOSVersion还是newOSVersion
  otaInstallTotalSeconds: number
  otaInstallPercent: number
  otaInstallLeftTime: number
  otaDownloadPercent: number
  hasOtaScheduleTime: boolean
  otaScheduleHour: number
  otaScheduleMinute: number
  otaScheduleTimeDesc: string
  msg: string
  title: string
}

const DEFAULT_ERROR_MSG = '服务器开小差啦'

const dateConfig = {
  'hour': {
    format: 'hh',
    caption: 'Hour',
    step: 1
  },
  'minute': {
    format: 'mm',
    caption: 'Min',
    step: 10
  }
}

const Topbar = ({
  otaTaskDetail,
  showLatest = false
}) => {
  const version = otaTaskDetail?.osVersion?.version
  return (
    <div className={styles.topbar}>
      <div className={styles.osInfo}>
        {/* <div className={styles.osName}>Xmart OS</div> */}
        {/* <div className={styles.osVersion}>{version ? `V${version}` : ''}</div> */}
        <div className={styles.osName}>{version ? `V${version}` : ''}</div>
        {showLatest && <div className={styles.latestTag}>车辆已是最新版本</div>}
      </div>
    </div>
  )
}

const InitCom = ({
  otaTaskDetail,
  processStatus,
  startUpgrade
}) => {
  const queryVin = useQuery().get('vin') || ''
  const scheduleTime = useRef(0)
  const descImgCount = useRef<number>(0)
  const [dayLabel, setDayLabel] = useState('')
  const [showTimePicker, setShowTimePicker] = useState(false)
  const [defaultScheduleTime, setDefaultScheduleTime] = useState(new Date())
  const [showScheduleInfo, setShowScheduleInfo] = useState(false)
  const [loadingDescImg, setLoadingDescImg] = useState(false)

  const installMinute = otaTaskDetail?.otaInstallTotalSeconds ? Math.ceil(otaTaskDetail?.otaInstallTotalSeconds / 60) : 0
  const upgradeTip = `本次升级约耗时${installMinute}分钟，升级期间车辆无法行驶，请在不影响正常道路行驶情况下升级`

  useEffect(() => {
    judgeScheduleTime()
    if (otaTaskDetail.osVersion?.introduces.length !== descImgCount.current) {
      handleImgLoad()
    }
  }, [otaTaskDetail])

  const handleImgLoad = () => {
    const totalImgCount = otaTaskDetail.osVersion?.introduces.length
    if (totalImgCount && totalImgCount > 0) {
      try {
        setLoadingDescImg(true)
        otaTaskDetail.osVersion.introduces.forEach((item, index) => {
          const img = new Image()
          img.src = item
          img.onload = () => {
            console.log(`img ${index} loaded`)
            if (descImgCount.current < totalImgCount) {
              descImgCount.current++
            }
            if (descImgCount.current === totalImgCount) {
              console.log('all img loaded')
              setLoadingDescImg(false)
            }
          }
          img.onerror = () => {
            setLoadingDescImg(false)
          }
        })
      } catch (error) {
        console.log('load img listener error -->', error)
        setLoadingDescImg(false)
      }
    }
  }

  const judgeScheduleTime = () => {
    let scheduleTimeTs = 0
    try {
      if (otaTaskDetail?.hasOtaScheduleTime) {
        scheduleTimeTs = new Date(otaTaskDetail?.scheduleTime).getTime()
        const now = Date.now()
        if (now >= scheduleTimeTs) {
          setShowScheduleInfo(false)
        } else {
          setShowScheduleInfo(true)
        }
      } else {
        setShowScheduleInfo(false)
      }
    } catch (error) {
      console.log('parse otaTaskDetail.scheduleTime error -->', error)
    }
  }

  const confirmScheduleTime = async () => {
    try {
      const scheduleDate = new Date(scheduleTime.current)
      const res: any = await saveScheduleTime({
        vin: queryVin,
        schedule: {
          beginHour: scheduleDate.getHours(),
          beginMinute: scheduleDate.getMinutes()
        }
      })
      if (res.code === 0) {
        // MessageBox.toast('预约成功')
        processStatus('saveSchedule')
        setShowTimePicker(false)
      } else {
        MessageBox.toast('预约失败，请重试！')
      }
    } catch (error) {
      MessageBox.toast('预约失败，请重试！')
      console.log('saveScheduleTime error -->', error)
    }
  }

  const confirmTimeSelect = () => {
    console.log('confirm time picker -->', scheduleTime.current)
    const now = Date.now()

    const _popConfirm = msg => {
      MessageBox.confirm(msg, {
        title: '提示',
        confirmButtonText: '重新预约',
        cancelButtonText: '取消'
      }).then(() => {
        // 点击重新预约 初始化默认时间选项
        popTimePicker()
      }).catch(() => {
        setShowTimePicker(false)
      })
    }

    let tip = ''
    if (scheduleTime.current <= now) {
      tip = '预约时间已过，请重新预约'
      _popConfirm(tip)
    } else if (scheduleTime.current - now < 10 * 60 * 1000) {
      tip = '非常抱歉，仅支持预约10分钟后的时间，请重新预约'
      _popConfirm(tip)
    } else {
      confirmScheduleTime()
    }
  }

  const popTimePicker = () => {
    const zeroTs = new Date().setHours(0, 0, 0, 0)
    const thresholdTsA = new Date().setHours(2, 50, 0, 0) // 今天02:50时间戳
    const thresholdTsB = new Date().setHours(3, 0, 0, 0) // 今天03:00时间戳
    const thresholdTsC = new Date().setHours(3, 30, 0, 0) // 今天03:30时间戳
    const now = Date.now() // 当前时间戳
    if (now >= zeroTs && now <= thresholdTsA) {
      // 设置为今天03:00
      scheduleTime.current = thresholdTsB
      setDayLabel('今天')
    } else if (now > thresholdTsA && now <= thresholdTsB) {
      // 设置为今天03:30
      scheduleTime.current = thresholdTsC
      setDayLabel('今天')
    } else {
      const tomorrowTs = thresholdTsB + 24 * 3600 * 1000
      scheduleTime.current = tomorrowTs
      setDayLabel('明天')
    }

    setDefaultScheduleTime(new Date(scheduleTime.current))
    setShowTimePicker(true)
  }

  const onChangeTimeSelect = value => {
    try {
      const valueTs = Date.parse(value)
      const oneDayTs = 24 * 3600 * 1000
      const todayTs = new Date().setHours(0, 0, 0, 0) // 今天0点时间戳
      const tomorrowTs = todayTs + oneDayTs // 明天0点时间戳
      const now = Date.now()
      // 把时间选择器返回的时间都设置为今天对应时刻
      let middleTime
      if (valueTs < todayTs) {
        middleTime = valueTs + Math.ceil((todayTs - valueTs) / oneDayTs) * oneDayTs
      } else if (valueTs >= tomorrowTs) {
        middleTime = valueTs - (Math.floor((valueTs - tomorrowTs) / oneDayTs) + 1) * oneDayTs
      } else {
        middleTime = valueTs
      }
      // 若选择时间早于当前时间，标签显示"明天"；选择时间晚于当前时间，标签显示"今天"
      if (middleTime < now) {
        scheduleTime.current = middleTime + oneDayTs
        setDayLabel('明天')
      } else {
        scheduleTime.current = middleTime
        setDayLabel('今天')
      }
    } catch (error) {
      console.log('onChangeTimeSelect err -->', error)
    }
  }

  const cancelSchedule = () => {
    MessageBox.confirm({
      title: '取消预约',
      confirmButtonText: '暂不',
      cancelButtonText: '确定',
      children: <div style={{textAlign: 'center'}}>{'确定要取消预约升级？'}</div>
    }).then(() => {
      // do nothing
    }).catch(async () => {
      try {
        const res: any = await cancelScheduleTime({
          vin: queryVin,
          otaTaskId: otaTaskDetail?.otaTaskId
        })
        if (res.code === 0) {
          // MessageBox.toast('已取消预约')
          processStatus('cancelSchedule')
        } else {
          MessageBox.toast('操作失败，请重试！')
        }
      } catch (error) {
        console.log('cancelScheduleTime error -->', error)
        MessageBox.toast('操作失败，请重试！')
      }
    })
  }

  const Picker = useMemo(() => (
    <DatePicker
      isPopup={false}
      showHeader={false}
      showFooter={false}
      dateConfig={dateConfig}
      value={defaultScheduleTime}
      onChange={onChangeTimeSelect}
    />
  ), [defaultScheduleTime])

  return (
    <div className={styles.wrap}>
      <div className={`${styles.scrollView} ${showScheduleInfo ? styles.isSchedule : styles.notSchedule}`}>
        <Topbar otaTaskDetail={otaTaskDetail} />
        {
          loadingDescImg ? (
            <div className={styles.loadingDescImg}>
              <div className={styles.loadingDescImgWrap}>
                <LoadingMore />
                <div className={styles.loadingText}>加载中...</div>
              </div>
            </div>
          ) : (
            <div className={styles.descImgList}>
              {
                otaTaskDetail?.osVersion?.introduces && (
                  otaTaskDetail?.osVersion?.introduces.map((item, index) => (
                    <img key={index} alt='' className={styles.descImg} src={item} />
                  ))
                )
              }
            </div>
          )
        }
      </div>
      <div className={styles.bottomWrap}>
        <div className={styles.scheduleBar}>
          <div className={styles.leftCont}>
            <img alt='' src={scheduleIcon} className={styles.icon} />
            <div className={styles.scheduleInfo}>
              {
                showScheduleInfo ? (
                  <>
                    <div className={styles.title}>{otaTaskDetail?.otaScheduleTimeDesc}</div>
                    <div className={styles.desc}>预计用时{installMinute}分钟</div>
                  </>
                ) : <div className={styles.title}>预约升级</div>
              }
            </div>
          </div>
          <div className={styles.rightCont}>
            {
              showScheduleInfo ?
                <div className={styles.cancelBtn} onClick={cancelSchedule}>取消</div> : (
                  <>
                    <div className={styles.scheduleSettingBtn} onClick={popTimePicker}>
                      <div className={styles.desc}>预计用时{installMinute}分钟</div>
                      <img className={styles.rightArrow} alt='' src={arrowRightIcon} />
                    </div>
                  </>
                )
            }
          </div>
        </div>
        <div className={styles.upgradeBtn} onClick={() => startUpgrade(upgradeTip)}>立即升级</div>
      </div>
      <PopupBottomModal
        visible={showTimePicker}
        title='预约升级'
        onClose={() => setShowTimePicker(false)}
      >
        <div className={styles.timePickerSubTitle}>预计用时{installMinute}分钟</div>
        <div className={styles.timePickerCont}>
          <div className={styles.day}>{dayLabel}</div>
          <div className={styles.timePickerWrap}>
            {Picker}
          </div>
        </div>
        <div className={styles.confirmBtn} onClick={confirmTimeSelect}>确定</div>
      </PopupBottomModal>
    </div>
  )
}

const LatestCom = ({ otaTaskDetail }) => {
  const descImgCount = useRef<number>(0)
  const [loadingDescImg, setLoadingDescImg] = useState(false)

  useEffect(() => {
    if (otaTaskDetail.osVersion?.introduces.length !== descImgCount.current) {
      handleImgLoad()
    }
  }, [otaTaskDetail])

  const handleImgLoad = () => {
    const totalImgCount = otaTaskDetail.osVersion?.introduces.length
    if (totalImgCount && totalImgCount > 0) {
      try {
        setLoadingDescImg(true)
        otaTaskDetail.osVersion.introduces.forEach((item, index) => {
          const img = new Image()
          img.src = item
          img.onload = () => {
            console.log(`img ${index} loaded`)
            if (descImgCount.current < totalImgCount) {
              descImgCount.current++
            }
            if (descImgCount.current === totalImgCount) {
              console.log('all img loaded')
              setLoadingDescImg(false)
            }
          }
          img.onerror = () => {
            setLoadingDescImg(false)
          }
        })
      } catch (error) {
        console.log('load img listener error -->', error)
        setLoadingDescImg(false)
      }
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.scrollView}>
        <Topbar otaTaskDetail={otaTaskDetail} showLatest />
        {
          loadingDescImg ? (
            <div className={styles.loadingDescImg}>
              <div className={styles.loadingDescImgWrap}>
                <LoadingMore />
                <div className={styles.loadingText}>加载中...</div>
              </div>
            </div>
          ) : (
            <div className={styles.descImgList}>
              {
                otaTaskDetail?.osVersion?.introduces && (
                  otaTaskDetail?.osVersion?.introduces.map((item, index) => (
                    <img key={index} alt='' className={styles.descImg} src={item} />
                  ))
                )
              }
            </div>
          )
        }
      </div>
    </div>
  )
}

const ConnectCom = ({
  status,
  otaTaskDetail
}) => {
  return (
    <div className={styles.wrap}>
      <Topbar otaTaskDetail={otaTaskDetail} />
      <div className={styles.connectCard}>
        {
          status === 'connect' ?
            <img alt='' src={loadingIcon} className={styles.loadingIcon} /> :
            <img alt='' src={finishedIcon} className={styles.finishedIcon} />
        }
        <div className={styles.title}>连接车辆</div>
      </div>
      <div className={`${styles.connectCard} ${styles.condition}`}>
        {
          status === 'connect' ?
            <img alt='' src={waitingIcon} className={styles.waitingIcon} /> :
            <img alt='' src={loadingIcon} className={styles.loadingIcon} />
        }
        <div className={styles.title}>检查升级条件</div>
      </div>
    </div>
  )
}

const UpgradeCom = ({ otaTaskDetail, theme }) => {
  const defaultColorMap = {
    progressColor: '#89B843',
    bgColor: 'rgba(0,0,0,0.1)'
  }
  const darkColorMap = {
    progressColor: '#7EA34B',
    bgColor: 'rgba(255,255,255,0.1)'
  }
  const colorMap = theme === 'dark' ? darkColorMap : defaultColorMap
  return (
    <div className={styles.commonWrap}>
      <div className={styles.upgradeCircle}>
        <Circle
          animate
          animationDuration='1s'
          responsive={false}
          size='180'
          lineWidth='20'
          progress={otaTaskDetail?.otaInstallPercent}
          progressColor={colorMap.progressColor}
          bgColor={colorMap.bgColor}
          textColor='#000000'
          textStyle={{}}
          percentSpacing={0}
          roundedStroke
          showPercentage={false}
          showPercentageSymbol
        />
      <div className={styles.percent}>{otaTaskDetail?.otaInstallPercent}</div>
      </div>
      <div className={styles.installingInfo}>
        <div className={styles.title}>正在安装</div>
        { otaTaskDetail?.otaInstallLeftTime >= 0 ? 
          <div className={styles.desc}>
            {otaTaskDetail?.otaInstallLeftTime <= 1 ? '即将升级完成' : `预计剩余${otaTaskDetail?.otaInstallLeftTime}分钟`}
          </div> : null
        }
        {/* <div className={styles.desc}>预计剩余{otaTaskDetail?.otaInstallLeftTime}分钟</div> */}
      </div>
      <div className={styles.installingTip}>提示：在更新过程中，你可以关闭应用，升级程序将在后台执行</div>
    </div>
  )
}

const SuccessCom = ({
  otaTaskDetail,
  goHome
}) => {
  return (
    <div className={styles.commonWrap}>
      <img className={styles.endStatusImgSuccess} alt='' src={successImg} />
      <div className={styles.endStatusTitle}>车辆升级成功</div>
      {/* <div className={styles.endStatusDesc}>已升级至Xmart OS V{otaTaskDetail?.osVersion?.version}</div> */}
      <div className={styles.endStatusDesc}>已升级至 V{otaTaskDetail?.osVersion?.version}</div>
      <div className={styles.finishBtn} onClick={goHome}>完成</div>
    </div>
  )
}

const ConnectFailCom = ({ retryUpgrade }) => {
  return (
    <div className={styles.commonWrap}>
      <img className={styles.endStatusImg} alt='' src={errorImg} />
      <div className={styles.endStatusTitle}>无法连接车辆</div>
      <div className={styles.finishBtn} onClick={retryUpgrade}>重试</div>
    </div>
  )
}

const ConditionFailCom = ({
  otaTaskDetail,
  retryUpgrade
}) => {
  return (
    <div className={styles.commonWrap}>
      <img className={styles.endStatusImg} alt='' src={errorImg} />
      <div className={styles.endStatusTitle}>升级条件不满足</div>
      <div className={styles.endStatusDesc}>{otaTaskDetail?.msg}</div>
      <div className={styles.finishBtn} onClick={retryUpgrade}>重试</div>
    </div>
  )
}

const UpgradeFailCom = ({ goHome }) => {
  return (
    <div className={styles.commonWrap}>
      <img className={styles.endStatusImg} alt='' src={errorImg} />
      <div className={styles.endStatusTitle}>车辆升级失败</div>
      <div className={styles.finishBtn} onClick={goHome}>确认</div>
    </div>
  )
}

const OtherFailCom = ({ otaTaskDetail }) => {
  return (
    <div className={styles.commonWrap}>
      <img className={styles.endStatusImg} alt='' src={errorImg} />
      <div className={styles.endStatusTitle}>{otaTaskDetail?.title}</div>
      <div className={styles.endStatusDesc}>{otaTaskDetail?.msg}</div>
      <div className={styles.finishBtn}>关闭</div>
    </div>
  )
}

const App = () => {
  // 颜色主题
  const themeModeContext = useContext(ThemeModeContext)
  const themeMode = themeModeContext?.mode || 'light'
  
  const queryVin = useQuery().get('vin') || ''
  const timer = useRef<any>(null)
  const connectTimeoutTimer = useRef<any>(null)

  const [status, setStatus] = useState('')
  const [otaTaskDetail, setOtaTaskDetail] = useState<OTATaskDetail>()
  const clickedUpgrade = useRef(false)

  useLayoutEffect(() => {
    setTitle('车辆升级')
  }, [])

  useEffect(() => {
    networkListener()

    processStatus('init')

    return componentWillUnmount
  }, [])

  useEffect(() => {
    if (status === 'connect') {
      connectTimeoutHandler()
    } else {
      clearConnectTimeoutTimer()
    }
    // eslint-disable-next-line
  }, [status])

  const networkListener = () => {
    if (!navigator.onLine) {
      MessageBox.toast('请检查手机网络')
    }

    window.addEventListener('offline', () => {
      MessageBox.toast('请检查手机网络')
    })
  }

  const setTitle = (title: string) => {
    document.title = title
  }

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }

  const componentWillUnmount = () => {
    clearTimer()
  }

  const goHome = () => {
    closeWebview()
  }

  const popConfirm = (msg = '') => {
    MessageBox.confirm(msg, {
      title: '提示',
      confirmButtonText: '确定',
      showCancelButton: false
    }).then(() => {
      goHome()
    }).catch(() => {})
  }

  const loopStatus = () => {
    timer.current = setTimeout(() => {
      processStatus('loop')
    }, 10000)
  }

  /**
   * 待安装状态下才会露出待升级入口
   * OTA任务的状态：
      0-无新版本
      1-待下载
      2-下载中
      3-下载暂停
      4-下载失败
      5-待安装
      6-前置条件校验中
      7-安装中
      8-安装成功
      9-安装失败
      99-已取消
   * @param whereCall
   */
  const processStatus = (whereCall = 'self') => {
    console.log('processStatus: ', whereCall)
    getTaskDetail({ vin: queryVin }).then(res => {
      if (res.code === 0) {
        const lastOtaStatus = sessionStorage.getItem('lastOtaStatus')
        const lastOSVersion = sessionStorage.getItem('lastOSVersion')

        const { data } = res
        const osVersion = data?.otaStatus === 0 ? data?.currentOSVersion : data?.newOSVersion
        setOtaTaskDetail({
          ...data,
          // osVersion: data?.otaStatus === 0 ? data?.currentOSVersion : data?.newOSVersion
          osVersion
        })
        setTitle('车辆升级')

        // 非就绪状态时，初始化用户已点击升级flag
        if (data.otaStatus !== 5) {
          clickedUpgrade.current = false
        }

        switch (data.otaStatus) {
          case 0:
            if (lastOtaStatus === '7') {
              // 缓存的版本号和本次获得的版本号一致，说明升级失败，反之升级成功
              if (lastOSVersion === data?.currentOSVersion?.version) {
                setStatus('upgradeFail')
              } else {
                setStatus('success')
              }
            } else {
              setStatus('latest')
              // setTitle('版本说明')
              const title = osVersion?.version ? `V${osVersion?.version}` : '版本说明'
              setTitle(title)
            }
            break
          case 5:
            if (whereCall === 'startUpgrade') {
              setStatus('checkCondition')
              loopStatus()
            } else if (lastOtaStatus === '6') {
              setStatus('conditionFail')
            } else if (lastOtaStatus === '7') {
              setStatus('upgradeFail')
            } else {
              // 因为车辆连接中这个状态是由前端触发点击设置的，后台不会返回，这里在用户点击了升级以后，防止当前的init轮询的误回退到初始状态
              if (!clickedUpgrade.current) {
                // setTitle('版本说明')
                const title = osVersion?.version ? `V${osVersion?.version}` : '版本说明'
                setTitle(title)
                setStatus('init')
                loopStatus()
              }
            }
            break
          case 6:
            setStatus('checkCondition')
            loopStatus()
            break
          case 7:
            setStatus('upgrade')
            loopStatus()

            // 存储本次轮询到的currentOSVersion，方便上面逻辑比较前后版本判断出是否升级成功/失败
            sessionStorage.setItem('lastOSVersion', data?.currentOSVersion?.version)
            break
          case 8:
            setStatus('success')
            break
          case 9:
            setStatus('upgradeFail')
            break
          default:
            setStatus('otherFail')
            break
        }
        // 存储本次轮询到的otaStatus，滴滴OTA后台会将升级条件检查失败等情况统一回到otaStatus=5待安装状态，这里方便上面逻辑比较前后状态判断出失败状态
        sessionStorage.setItem('lastOtaStatus', data?.otaStatus)
      } else {
        popConfirm(res?.msg || res?.message || DEFAULT_ERROR_MSG)
      }
    }).catch(error => {
      popConfirm(error?.msg || error?.message || DEFAULT_ERROR_MSG)
    })
  }

  /**
   * 车机无网或者其他连接车辆失败的场景下，滴滴后台没有相应字段返回，这里约定10s还没进入后续步骤则提示【车辆连接超时，请稍后重试】
   * 考虑后续网络请求耗时以及前端10s轮询一次的时间，这里设置延时15s
   */
  const connectTimeoutHandler = () => {
    connectTimeoutTimer.current = setTimeout(() => {
      console.log('connectTimeoutHandler status', status)
      if (status === 'connect') {
        popConfirm('车辆连接超时，请稍后重试')
        clearTimer()
      }
    }, 15 * 1000)
  }

  const clearConnectTimeoutTimer = () => {
    if (connectTimeoutTimer.current) {
      clearTimeout(connectTimeoutTimer.current)
      connectTimeoutTimer.current = null
    }
  }

  const startUpgrade = upgradeTip => {
    try {
      MessageBox.confirm(upgradeTip, {
        title: '温馨提示',
        confirmButtonText: '立即升级',
        cancelButtonText: '暂不升级'
      }).then(async () => {
        clearTimer()
        clickedUpgrade.current = true
        setTitle('车辆升级')

        setStatus('connect')

        const awakenRes: any = await awakenVehicle({ vin: queryVin })
        if (awakenRes.code === 0) {
          const upgradeRes: any = await startConditionCheck({
            vin: queryVin,
            otaTaskId: otaTaskDetail?.otaTaskId
          })
          if (upgradeRes.code === 0) {
            processStatus('startUpgrade')
          } else {
            setStatus('conditionFail')
          }
        } else {
          setStatus('connectFail')
        }
      }).catch(() => {})
    } catch (error) {
      popConfirm(DEFAULT_ERROR_MSG)
    }
  }

  const retryUpgrade = () => {
    setStatus('init')
    processStatus('retryUpgrade')
  }

  const COMPONENT_MAP = {
    init: <InitCom
      otaTaskDetail={otaTaskDetail}
      processStatus={processStatus}
      startUpgrade={startUpgrade}
    />,
    success: <SuccessCom otaTaskDetail={otaTaskDetail} goHome={goHome} />,
    latest: <LatestCom otaTaskDetail={otaTaskDetail} />,
    connect: <ConnectCom status={status} otaTaskDetail={otaTaskDetail} />,
    checkCondition: <ConnectCom status={status} otaTaskDetail={otaTaskDetail} />,
    upgrade: <UpgradeCom otaTaskDetail={otaTaskDetail} theme={themeMode}/>,
    connectFail: <ConnectFailCom retryUpgrade={retryUpgrade} />,
    conditionFail: <ConditionFailCom otaTaskDetail={otaTaskDetail} retryUpgrade={retryUpgrade} />,
    upgradeFail: <UpgradeFailCom goHome={goHome} />,
    otherFail: <OtherFailCom otaTaskDetail={otaTaskDetail} />
  }

  return (
    <>
      {COMPONENT_MAP[status] || <LoadingPage />}
    </>
  )
}

export default App
